import { env } from 'next-runtime-env';

export type RemoteConfig = {
  url?: string;
  scope: string;
  version: string;
  versions?: Record<string, string>;
  lazyLoad?: boolean;
  dependencies?: string[];
  entryFile?: string;
  manifestUrl?: string;
  isLibrary?: boolean;
  /**
   * Public URL prefix(es) for Next.js rewrites (e.g. `/funding` → internally `/m/funding`).
   * Must include every manifest `hostRoutePrefix` value for this remote (string or array).
   */
  hostRoutePrefix?: string | string[];
  /**
   * Host sidebar placement for this remote's manifest-driven menu block (1-based).
   * `1` = before Facilities, `2` = before Securitization, `3` = before Dashboard, and so on through the fixed host sections.
   * Omit or invalid values → append after all host sections (last).
   */
  menuPosition?: number;
};

const normalizeConfigHostRouteToken = (value: string): string | null => {
  const t = value.trim().replace(/\/$/, '');
  if (!t.startsWith('/') || t.length < 2) {
    return null;
  }
  return t;
};

/** Normalized public path prefixes from federation config (for rewrites and manifest alignment). */
export function getRemoteConfigHostRoutePrefixes(remote: RemoteConfig): string[] {
  const raw = remote.hostRoutePrefix;
  if (raw === undefined || raw === null) {
    return [];
  }
  if (typeof raw === 'string') {
    const n = normalizeConfigHostRouteToken(raw);
    return n ? [n] : [];
  }
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') {
      continue;
    }
    const n = normalizeConfigHostRouteToken(item);
    if (n && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

export interface ComponentConfig {
  module: string;
  props?: Record<string, string>;
}

export interface FederationConfig {
  version: string;
  environments: Record<
    string,
    {
      remotes: Record<string, RemoteConfig>;
    }
  >;
  features?: {
    'lazy-loading'?: boolean;
    'error-boundaries'?: boolean;
    caching?: boolean;
    'retry-attempts'?: number;
    timeout?: number;
    'style-loader-remote'?: string;
  };
}

export interface ConfigLoaderOptions {
  environment?: string;
  configUrl?: string;
  fallbackConfig?: FederationConfig;
  cacheTTL?: number;
  retryAttempts?: number;
  enableHotReload?: boolean;
}

export const DEFAULT_ENVIRONMENT = 'production';

/** Embedded copy of `public/federation-config.json` used when no config can be fetched. */
export const PUBLIC_FALLBACK_CONFIG: FederationConfig = {
  version: '1.0.0',
  environments: {
    development: {
      remotes: {
        'aga-components': {
          scope: 'aga-components',
          version: 'default',
          lazyLoad: false,
          isLibrary: true,
          versions: { default: 'https://storybook.atlas-sp.com' },
        },
        'aga-dealcard': {
          scope: 'aga-dealcard',
          version: 'default',
          versions: { default: 'https://dealcarddev.atlas-sp.com' },
          dependencies: ['aga-components'],
        },
      },
    },
    dev: {
      remotes: {
        'aga-components': {
          scope: 'aga-components',
          version: 'default',
          lazyLoad: false,
          isLibrary: true,
          versions: { default: 'https://storybook.atlas-sp.com' },
        },
        'aga-dealcard': {
          scope: 'aga-dealcard',
          version: 'default',
          versions: { default: 'https://dealcarddev.atlas-sp.com' },
          dependencies: ['aga-components'],
        },
      },
    },
    uat: {
      remotes: {
        'aga-components': {
          scope: 'aga-components',
          version: 'default',
          lazyLoad: false,
          isLibrary: true,
          versions: { default: 'https://agacomponentsuat.atlas-sp.com' },
        },
        'aga-dealcard': {
          scope: 'aga-dealcard',
          version: 'default',
          versions: { default: 'https://agadealcarduat.atlas-sp.com' },
          dependencies: ['aga-components'],
        },
      },
    },
    production: {
      remotes: {
        'aga-components': {
          scope: 'aga-components',
          version: 'default',
          lazyLoad: false,
          isLibrary: true,
          versions: { default: 'https://agacomponents.atlas-sp.com' },
        },
        'aga-dealcard': {
          scope: 'aga-dealcard',
          version: 'default',
          versions: { default: 'https://agadealcardprod.atlas-sp.com' },
          dependencies: ['aga-components'],
        },
      },
    },
    dr: {
      remotes: {
        'aga-components': {
          scope: 'aga-components',
          version: 'default',
          lazyLoad: false,
          isLibrary: true,
          versions: { default: 'https://agacomponentsdr.atlas-sp.com' },
        },
        'aga-dealcard': {
          scope: 'aga-dealcard',
          version: 'default',
          versions: { default: 'https://agadealcarddr.atlas-sp.com' },
          dependencies: ['aga-components'],
        },
      },
    },
  },
  features: {
    'lazy-loading': true,
    'error-boundaries': true,
    caching: true,
    'retry-attempts': 3,
    timeout: 10000,
    'style-loader-remote': 'aga-components',
  },
};

export class FederationConfigLoader {
  private config: FederationConfig | null = null;
  private cache = new Map<string, { data: FederationConfig; timestamp: number }>();
  private loadPromise: Promise<FederationConfig> | null = null;
  private options: Required<ConfigLoaderOptions>;
  private environment: string;

  constructor(options: ConfigLoaderOptions = {}) {
    this.options = {
      environment: options.environment || this.getEnvironment(),
      configUrl: options.configUrl || this.resolveConfigSource(),
      fallbackConfig: options.fallbackConfig || PUBLIC_FALLBACK_CONFIG,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000,
      retryAttempts: options.retryAttempts || 3,
      enableHotReload: options.enableHotReload ?? true,
    };
    this.environment = this.options.environment;

    if (typeof window !== 'undefined' && this.options.enableHotReload) {
      this.setupHotReload();
    }
  }

  /**
   * Resolves the current environment key. Guarantees a non-empty string is
   * returned: prefers `NEXT_PUBLIC_ENV`, falls back to `NODE_ENV`-based
   * detection, and ultimately defaults to `'production'`.
   */
  getEnvironment(): string {
    const runtimeVal = env('NEXT_PUBLIC_ENV');
    const buildVal = process.env.NEXT_PUBLIC_ENV;
    const value = runtimeVal || buildVal;
    const trimmed = typeof value === 'string' ? value.trim() : '';

    if (trimmed) {
      return trimmed;
    }

    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      return 'dev';
    }

    return DEFAULT_ENVIRONMENT;
  }

  resolveConfigSource(): string {
    const envConfig = env('NEXT_PUBLIC_FEDERATION_CONFIG');
    if (envConfig) {
      const trimmed = envConfig.trim();
      if (!trimmed.startsWith('{')) {
        return trimmed;
      }
    }
    return '/federation-config.json';
  }

  getInlineConfig(): FederationConfig | null {
    const envConfig = env('NEXT_PUBLIC_FEDERATION_CONFIG');
    if (!envConfig) return null;

    const trimmed = envConfig.trim();
    if (!trimmed.startsWith('{')) return null;

    try {
      return JSON.parse(trimmed) as FederationConfig;
    } catch (e) {
      console.error('❌ Failed to parse NEXT_PUBLIC_FEDERATION_CONFIG as JSON:', e);
      return null;
    }
  }

  private setupHotReload() {
    if (this.environment === 'development') {
      setInterval(() => {
        this.loadConfig(true);
      }, 10000);
    }
  }

  /**
   * If the requested environment key does not exist in the config, try to find
   * the best match: first `'production'`, then the first available key.
   * Returns the original key if it exists, or the resolved fallback key.
   */
  resolveEnvironmentKey(config: FederationConfig): string {
    if (config.environments[this.environment]) {
      return this.environment;
    }

    const available = Object.keys(config.environments);
    console.warn(`⚠️ Environment "${this.environment}" not found in config, available: ${available.join(', ')}`);

    if (config.environments[DEFAULT_ENVIRONMENT]) {
      console.warn(`↪ Falling back to "${DEFAULT_ENVIRONMENT}" environment`);
      return DEFAULT_ENVIRONMENT;
    }

    if (available.length > 0) {
      console.warn(`↪ Falling back to first available environment: "${available[0]}"`);
      return available[0];
    }

    return this.environment;
  }

  validateConfig(rawConfig: any): FederationConfig {
    if (!rawConfig || typeof rawConfig !== 'object') {
      throw new Error('Invalid config: must be an object');
    }

    if (!rawConfig.version) {
      throw new Error('Invalid config: missing version');
    }

    if (!rawConfig.environments || typeof rawConfig.environments !== 'object') {
      throw new Error('Invalid config: missing or invalid environments');
    }

    const validated = rawConfig as FederationConfig;

    // Auto-correct the environment key if the requested one is missing
    this.environment = this.resolveEnvironmentKey(validated);

    return validated;
  }

  async loadConfig(force = false): Promise<FederationConfig> {
    if (!force && this.config) {
      return this.config;
    }

    if (!force && this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.fetchConfigWithRetry();
    const newConfig = await this.loadPromise;
    this.config = newConfig;
    this.loadPromise = null;

    // Clear components cache when config changes
    this._componentsCache = {};

    // Emit config update event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('federation-config-updated', { detail: newConfig }));
    }

    return newConfig;
  }

  private async fetchConfigWithRetry(): Promise<FederationConfig> {
    const inlineConfig = this.getInlineConfig();
    if (inlineConfig) {
      console.log('✅ Federation config loaded from NEXT_PUBLIC_FEDERATION_CONFIG (inline JSON)');
      const validatedConfig = this.validateConfig(inlineConfig);
      const cacheKey = `inline-env-${this.environment}`;
      this.cache.set(cacheKey, { data: validatedConfig, timestamp: Date.now() });
      return validatedConfig;
    }

    const source = this.options.configUrl;
    console.log(`Loading federation config from ${source} (attempt 1/${this.options.retryAttempts})`);

    for (let attempt = 1; attempt <= this.options.retryAttempts; attempt++) {
      try {
        const cacheKey = `${source}-${this.environment}`;
        const cached = this.cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < this.options.cacheTTL) {
          console.log('✅ Federation config loaded from cache');
          return cached.data;
        }

        const response = await fetch(source, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const rawConfig = await response.json();
        const validatedConfig = this.validateConfig(rawConfig);

        this.cache.set(cacheKey, {
          data: validatedConfig,
          timestamp: Date.now(),
        });

        console.log(`✅ Federation config loaded successfully (v${validatedConfig.version})`);
        return validatedConfig;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error(`❌ Federation config load attempt ${attempt} failed:`, errorMessage);

        if (attempt === this.options.retryAttempts) {
          console.warn('⚠️ All attempts failed, falling back to default config');
          return this.validateConfig(this.options.fallbackConfig);
        }

        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }

    return this.validateConfig(this.options.fallbackConfig);
  }

  getRemoteConfig(remoteName: string): RemoteConfig | null {
    const envConfig = this.config?.environments[this.environment];
    if (!envConfig) return null;
    return envConfig.remotes[remoteName] || null;
  }

  getComponentConfig(remoteName: string, componentName: string): ComponentConfig | null {
    // Since we don't have static component definitions, create a dynamic one
    return { module: `./${componentName}` };
  }

  getAllRemotes(): Record<string, RemoteConfig> {
    const envConfig = this.config?.environments[this.environment];
    return envConfig?.remotes || {};
  }

  // Per-remote cache of exposed modules discovered from remoteEntry.js
  private _componentsCache: Record<string, Record<string, { module: string }>> = {};

  /** Discover and cache exposed modules for a single remote (federated load path). */
  async getRemoteComponents(remoteName: string): Promise<Record<string, { module: string }>> {
    if (remoteName in this._componentsCache) {
      return this._componentsCache[remoteName];
    }

    const remoteConfig = this.getRemoteConfig(remoteName);
    if (!remoteConfig) {
      this._componentsCache[remoteName] = {};
      return {};
    }

    try {
      console.log(`Discovering components for ${remoteName}...`);

      const { url } = resolveRemoteUrl(remoteConfig);
      if (!url) {
        console.warn(`No URL found for remote ${remoteName}`);
        this._componentsCache[remoteName] = {};
        return {};
      }

      let components = await this.extractComponentsFromText(url, remoteName);

      if (Object.keys(components).length > 0) {
        console.log(`Discovered ${Object.keys(components).length} components in ${remoteName}`);
      } else {
        console.log(`Text extraction found no components for ${remoteName}, trying dynamic discovery...`);
        components = await this.extractComponentsFromRemote(url, remoteName);

        if (Object.keys(components).length > 0) {
          console.log(`Dynamically discovered ${Object.keys(components).length} components in ${remoteName}`);
        } else {
          console.warn(`No components discovered for ${remoteName}`);
        }
      }

      this._componentsCache[remoteName] = components;
      return components;
    } catch (e) {
      console.warn(`Failed to process ${remoteName}:`, e);
      this._componentsCache[remoteName] = {};
      return {};
    }
  }

  /** Dashboard-only: fetch and cache all exposed modules from every remoteEntry.js */
  async getAllComponents(): Promise<Record<string, Record<string, { module: string }>>> {
    const remotes = this.getAllRemotes();
    const remoteNames = Object.keys(remotes);

    if (remoteNames.length > 0 && remoteNames.every(name => name in this._componentsCache)) {
      return { ...this._componentsCache };
    }

    await Promise.all(remoteNames.map(remoteName => this.getRemoteComponents(remoteName)));
    return { ...this._componentsCache };
  }

  private async extractComponentsFromText(
    remoteUrl: string,
    remoteName: string,
  ): Promise<Record<string, { module: string }>> {
    try {
      const res = await fetch(remoteUrl, { cache: 'force-cache' });
      const text = await res.text();

      const components: Record<string, { module: string }> = {};

      // Look for exposesMap pattern in @module-federation/vite
      const exposesMapMatch = text.match(/exposesMap\s*=\s*{([\s\S]*?)};?\s*const/);
      if (exposesMapMatch) {
        // Extract module names from the exposesMap
        const moduleMatches = exposesMapMatch[1].matchAll(/['"](\.[^'"]+)['"]:\s*async/g);
        for (const match of moduleMatches) {
          const moduleName = match[1];
          const componentName = moduleName.replace('./', '');
          components[componentName] = { module: moduleName };
        }
      }

      return components;
    } catch (e) {
      console.warn(`Text extraction failed for ${remoteName}:`, e);
      return {};
    }
  }

  private async extractComponentsFromRemote(
    remoteUrl: string,
    remoteName: string,
  ): Promise<Record<string, { module: string }>> {
    try {
      console.log(`Attempting to dynamically load remote for inspection: ${remoteName}`);
      const remote = await this.loadRemoteForInspection(remoteUrl);

      if (remote) {
        const components: Record<string, { module: string }> = {};

        // Check if the remote exposes a manifest or component list
        if (remote.__federationManifest) {
          // Some remotes might expose their manifest directly
          const manifest = remote.__federationManifest;
          if (manifest.exposes) {
            for (const [key, value] of Object.entries(manifest.exposes)) {
              const componentName = key.replace('./', '');
              components[componentName] = { module: key };
            }
            return components;
          }
        }

        // Try to get exposesMap (used by @module-federation/vite)
        if (remote.exposesMap) {
          for (const [key] of Object.entries(remote.exposesMap)) {
            const componentName = key.replace('./', '');
            components[componentName] = { module: key };
          }
          return components;
        }

        // If the remote has a get method, we can try to probe for a special manifest module
        if (remote.get && typeof remote.get === 'function') {
          try {
            // Try to get a manifest module if exposed
            const manifest = await remote.get('./__manifest');
            if (manifest && typeof manifest === 'function') {
              const manifestData = manifest();
              if (manifestData && manifestData.modules) {
                for (const moduleName of manifestData.modules) {
                  const componentName = moduleName.replace('./', '');
                  components[componentName] = { module: moduleName };
                }
                return components;
              }
            }
          } catch (e) {
            // Manifest module not available, that's ok
            console.log(`No manifest module found for ${remoteName}`);
          }
        }

        console.log(`Could not discover component list for ${remoteName} dynamically`);
        return components;
      }
    } catch (e) {
      console.warn(`Dynamic loading failed for ${remoteName}:`, e);
    }

    return {};
  }

  private async loadRemoteForInspection(remoteUrl: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const globalName = `__inspect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const moduleScript = `
        import * as remote from '${remoteUrl}';
        window.${globalName} = remote;
        window.dispatchEvent(new CustomEvent('${globalName}_loaded'));
      `;

      const blob = new Blob([moduleScript], { type: 'application/javascript' });
      const scriptUrl = URL.createObjectURL(blob);
      const script = document.createElement('script');
      script.type = 'module';
      script.src = scriptUrl;

      let cleanupCalled = false;
      const cleanup = () => {
        if (cleanupCalled) return;
        cleanupCalled = true;

        try {
          URL.revokeObjectURL(scriptUrl);
        } catch (e) {
          console.warn('Failed to revoke object URL:', e);
        }

        try {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        } catch (e) {
          console.warn('Failed to remove script element:', e);
        }

        try {
          window.removeEventListener(`${globalName}_loaded`, handleLoad);
          script.removeEventListener('error', handleError);
        } catch (e) {
          console.warn('Failed to remove event listeners:', e);
        }
      };

      const handleLoad = () => {
        cleanup();
        const remote = (window as any)[globalName];
        try {
          delete (window as any)[globalName];
        } catch (e) {
          console.warn('Failed to delete global variable:', e);
        }
        resolve(remote);
      };

      const handleError = (event: any) => {
        cleanup();
        reject(new Error(`Failed to load remote for inspection: ${event.message || 'Unknown error'}`));
      };

      window.addEventListener(`${globalName}_loaded`, handleLoad);
      script.addEventListener('error', handleError);

      // Timeout after 5 seconds
      setTimeout(() => {
        cleanup();
        reject(new Error('Timeout loading remote for inspection'));
      }, 5000);

      try {
        document.head.appendChild(script);
      } catch (e: any) {
        cleanup();
        reject(new Error(`Failed to append script: ${e.message}`));
      }
    });
  }

  getPreset(presetName: string): string[] {
    // Presets are not supported without static component definitions
    return [];
  }

  getFeatures() {
    return this.config?.features || {};
  }

  updateConfig(newConfig: any): void {
    try {
      const validatedConfig = this.validateConfig(newConfig);
      this.config = validatedConfig;

      // Clear cache
      this.cache.clear();
      this._componentsCache = {};

      // Emit update event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('federation-config-updated', { detail: validatedConfig }));
      }

      console.log('✅ Federation config updated successfully');
    } catch (error) {
      console.error('❌ Failed to update federation config:', error);
      throw error;
    }
  }

  getCurrentConfig(): FederationConfig | null {
    return this.config;
  }

  clearCache(): void {
    this.cache.clear();
    this._componentsCache = {};
  }
}

// Helper function to normalize URL - append entry file if not present
function normalizeRemoteUrl(url: string, entryFile: string = 'remoteEntry.js'): string {
  // Remove trailing slash
  const cleanUrl = url.replace(/\/$/, '');

  // Check if URL already ends with the entry file (with or without leading slash)
  if (cleanUrl.endsWith(`/${entryFile}`) || cleanUrl.endsWith(entryFile)) {
    return cleanUrl;
  }

  // Also check for default remoteEntry.js to preserve existing configs
  if (cleanUrl.endsWith('/remoteEntry.js') || cleanUrl.endsWith('remoteEntry.js')) {
    return cleanUrl;
  }

  // Append entry file
  return `${cleanUrl}/${entryFile}`;
}

// Utility function to resolve remote URL with version support
export function resolveRemoteUrl(
  remoteConfig: RemoteConfig,
  version?: string,
  fallbackVersion?: string,
): { url: string; version: string } {
  // Get entry file from config or use default
  const entryFile = remoteConfig.entryFile || 'remoteEntry.js';

  // Handle new-style remotes with versions
  if (remoteConfig.versions) {
    const requestedVersion = version || remoteConfig.version || 'default';
    const urlFromVersions = remoteConfig.versions[requestedVersion];

    if (urlFromVersions) {
      return {
        url: normalizeRemoteUrl(urlFromVersions, entryFile),
        version: requestedVersion,
      };
    }

    // Fallback version logic
    if (fallbackVersion && remoteConfig.versions[fallbackVersion]) {
      console.warn(`Requested version "${requestedVersion}" not found for remote, using fallback "${fallbackVersion}"`);
      return {
        url: normalizeRemoteUrl(remoteConfig.versions[fallbackVersion], entryFile),
        version: fallbackVersion,
      };
    }

    // Use default if available
    if (remoteConfig.versions.default) {
      console.warn(`Requested version "${requestedVersion}" not found for remote, using default`);
      return {
        url: normalizeRemoteUrl(remoteConfig.versions.default, entryFile),
        version: 'default',
      };
    }

    // Use first available version
    const firstVersion = Object.keys(remoteConfig.versions)[0];
    if (firstVersion) {
      console.warn(
        `Requested version "${requestedVersion}" not found for remote, using first available "${firstVersion}"`,
      );
      return {
        url: normalizeRemoteUrl(remoteConfig.versions[firstVersion], entryFile),
        version: firstVersion,
      };
    }

    throw new Error(`No valid versions found for remote`);
  }

  // Handle legacy-style remotes with single URL
  if (remoteConfig.url) {
    return {
      url: normalizeRemoteUrl(remoteConfig.url, entryFile),
      version: version || 'default',
    };
  }

  throw new Error('Remote configuration has no URL or versions');
}

// Global instance
export const federationConfigLoader = new FederationConfigLoader();

export const createConfigLoader = (options: ConfigLoaderOptions) => {
  return new FederationConfigLoader(options);
};

export const createRemoteConfigLoader = (remoteUrl: string, options?: Omit<ConfigLoaderOptions, 'configUrl'>) => {
  return new FederationConfigLoader({
    ...options,
    configUrl: remoteUrl,
  });
};

export const createAPIConfigLoader = (apiEndpoint: string, options?: ConfigLoaderOptions) => {
  return new FederationConfigLoader({
    ...options,
    configUrl: apiEndpoint,
  });
};
